import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { useTranslation } from 'react-i18next'
import {
  computeSsdrMutation,
  copyAndUpdateControlDataStructureMutation,
  createMapSnapshotMutation,
  deleteControlDataSchemaMutation,
  deleteSnapshotMutation,
  getControlDataSchemasOptions,
  getControlDataSchemasQueryKey,
  getSnapshotListWithOverridesOptions,
  getSnapshotListWithOverridesQueryKey,
  syncMapsMutation,
  updateControlDataStructureMutation,
} from '@/api/main/@tanstack/react-query.gen'
import type { SyncResponse } from '@/api/main/types.gen'
import { errorMessage } from '@/utils/other'
import { sortControlSchemas } from './sortControlSchemas'

export const useMapSnapshots = () => {
  const { t } = useTranslation('mapSnapshots')
  const queryClient = useQueryClient()

  const snapshotsQuery = useSuspenseQuery(getSnapshotListWithOverridesOptions())
  const schemasQuery = useSuspenseQuery(getControlDataSchemasOptions())

  const sortedSchemas = sortControlSchemas(schemasQuery.data)

  const invalidateSnapshots = () => queryClient.invalidateQueries({ queryKey: getSnapshotListWithOverridesQueryKey() })
  const invalidateSchemas = () => queryClient.invalidateQueries({ queryKey: getControlDataSchemasQueryKey() })

  // Control data schema toasts live in the `controlData` namespace.
  const notifyCd = (titleKey: string, message: string, color: 'green' | 'red') =>
    notifications.show({ title: t(titleKey, { ns: 'controlData' }), message, color })

  const createSnapshot = useMutation({
    ...createMapSnapshotMutation(),
    onSuccess: (_data, variables) => {
      invalidateSnapshots()
      notifications.show({
        title: t('createSnapshot'),
        message: t('snapshotCreated', { name: variables.body.snapshot_name }),
        color: 'green',
      })
    },
    onError: error => {
      notifications.show({
        title: t('createSnapshot'),
        message: errorMessage(error, t('createFailed')),
        color: 'red',
      })
    },
  })

  const syncSnapshot = useMutation({
    ...syncMapsMutation(),
    onSuccess: (_data, variables) => {
      invalidateSchemas()
      notifications.show({
        title: t('syncSnapshot'),
        message: t('snapshotSynced', {
          snapshot: variables.body.snapshot_from,
          schema: variables.body.destination_schema,
        }),
        color: 'green',
      })
    },
    onError: error => {
      notifications.show({
        title: t('syncSnapshot'),
        message: errorMessage(error, t('syncFailed')),
        color: 'red',
      })
    },
  })

  const deleteSnapshot = useMutation({
    ...deleteSnapshotMutation(),
    onSuccess: (_data, variables) => {
      invalidateSnapshots()
      notifications.show({
        title: t('deleteSnapshot'),
        message: t('snapshotDeleted', { name: variables.body.snapshot_name }),
        color: 'green',
      })
    },
    onError: error => {
      notifications.show({
        title: t('deleteSnapshot'),
        message: errorMessage(error, t('deleteFailed')),
        color: 'red',
      })
    },
  })

  const copySchema = useMutation({
    ...copyAndUpdateControlDataStructureMutation(),
    onSuccess: (_data, variables) => {
      invalidateSchemas()
      const { source_schema: source, destination_schema: destination } = variables.body
      notifyCd('copySchemaAction', t('schemaCopied', { ns: 'controlData', source, destination }), 'green')
    },
    onError: error => notifyCd('copySchemaAction', errorMessage(error, t('copyFailed', { ns: 'controlData' })), 'red'),
  })

  const updateSchema = useMutation({
    ...updateControlDataStructureMutation(),
    onSuccess: (_data, variables) => {
      invalidateSchemas()
      const name = variables.headers?.['city-sense-control-data-schema'] ?? ''
      notifyCd('updateSchemaAction', t('schemaUpdated', { ns: 'controlData', name }), 'green')
    },
    onError: error =>
      notifyCd('updateSchemaAction', errorMessage(error, t('updateFailed', { ns: 'controlData' })), 'red'),
  })

  const deleteSchema = useMutation({
    ...deleteControlDataSchemaMutation(),
    onSuccess: (_data, variables) => {
      invalidateSchemas()
      notifyCd('deleteSchemaAction', t('schemaDeleted', { ns: 'controlData', name: variables.query.schema }), 'green')
    },
    onError: error =>
      notifyCd('deleteSchemaAction', errorMessage(error, t('deleteFailed', { ns: 'controlData' })), 'red'),
  })

  const computeSsdr = useMutation({
    ...computeSsdrMutation(),
    onSuccess: data =>
      notifyCd('computeSsdrAction', data?.message || t('ssdrComputed', { ns: 'controlData' }), 'green'),
    onError: error => notifyCd('computeSsdrAction', errorMessage(error, t('ssdrFailed', { ns: 'controlData' })), 'red'),
  })

  return {
    snapshots: snapshotsQuery.data,
    schemas: sortedSchemas,
    createSnapshot,
    syncSnapshot,
    deleteSnapshot,
    copySchema,
    updateSchema,
    deleteSchema,
    computeSsdr,
  }
}

export type SyncResult = SyncResponse[]
